enum TrackType {
  IMPRESSION = 'simulation:track_impression',
  CLICK = 'simulation:track_click',
  ENGAGEMENT = 'simulation:track_engagement',
  AB_EXPERIMENT = 'simulation:track_ab_experiment',
}

export type UnitLocation =
  | 'header'
  | 'footer'
  | 'inline'
  | 'modal'
  | 'header_sticky'
  | 'inline_sticky'
  | 'sidebar_sticky'
  | 'assistant'
  | 'tyce'
  | null

export type EngagementEventTypes =
  | 'audio_current_time_changed'
  | 'audio_pause'
  | 'audio_play'
  | 'audio_complete'
  | 'audio_percent_played'
  | 'audio_duration'
  | 'bookmark'
  | 'bookmark_select'
  | 'bookmark_deselect'
  | 'click'
  | 'community_download'
  | 'connect_start'
  | 'connect'
  | 'deselect'
  | 'dismiss'
  | 'donate'
  | 'download'
  | 'follow'
  | 'inquiry_start'
  | 'inquiry_submit'
  | 'message_request'
  | 'print'
  | 'rating'
  | 'search'
  | 'select'
  | 'share_start'
  | 'share'
  | 'signup_start'
  | 'subscribe'
  | 'video_complete'
  | 'video_duration'
  | 'video_percentage_played'
  | 'video_play'
  | 'video_pause'
  | 'video_replay'
  | 'video_mute'
  | 'video_unmute'
  | 'skip'
  | 'back'

export type TrackBaseEvent = {
  /**
   * Component_id. If the object is not Compose-based, then the string name of the component.
   */
  unitName: string
  /**
   * Area of page that unit is located on
   */
  unitLocation: UnitLocation
  /**
   * If a unit can take multiple forms, shapes, or styles, the version in question will be tracked here.
   */
  unitVariation?: string
}

export type TrackImpressionData = TrackBaseEvent

export type TrackClickData = TrackBaseEvent & {
  clickType: 'button' | 'text'
  /**
   * Name of button. Null if click_type = text
   * @default null
   */
  buttonName?: string | null
  /**
   * Text contained within button. Null if click_type = text.
   * @default null
   */
  buttonText?: string
  /**
   * Text for hyperlink. Null if click_type = button
   * @default null
   */
  linkText?: string | null
  /**
   * True if link is to another page on understood.org
   * @default false
   */
  isContent?: boolean
  /**
   * True if link takes user off of understood.org
   * @default false
   */
  isExternal?: boolean
  /**
   * True if link causes a modal pop-up
   * @default false
   */
  isModal?: boolean
}

export type TrackEngagementData = TrackBaseEvent & {
  eventType: EngagementEventTypes
}

export type TrackAbExperimentData = TrackBaseEvent & {
  experimentName: string
  variant: string
}

export function createEventImpression(data: TrackImpressionData) {
  return new CustomEvent<TrackImpressionData>(TrackType.IMPRESSION, { detail: data, bubbles: true, composed: true })
}
export function createEventClick(data: TrackClickData) {
  return new CustomEvent<TrackClickData>(TrackType.CLICK, { detail: data, bubbles: true, composed: true })
}
export function createEventEngagement(data: TrackEngagementData) {
  return new CustomEvent<TrackEngagementData>(TrackType.ENGAGEMENT, { detail: data, bubbles: true, composed: true })
}
export function createEventAbExperiment(data: TrackAbExperimentData) {
  return new CustomEvent<TrackAbExperimentData>(TrackType.AB_EXPERIMENT, {
    detail: data,
    bubbles: true,
    composed: true,
  })
}

export default function useTrackingWithDom(element: HTMLElement | null) {
  const trackImpression = (data: TrackImpressionData) => {
    element?.dispatchEvent(createEventImpression(data))
  }

  const trackClick = (data: TrackClickData) => {
    element?.dispatchEvent(createEventClick(data))
  }

  const trackEngagement = (data: TrackEngagementData) => {
    element?.dispatchEvent(createEventEngagement(data))
  }

  const trackAbExperiment = (data: TrackAbExperimentData) => {
    element?.dispatchEvent(createEventAbExperiment(data))
  }

  return {
    trackImpression,
    trackClick,
    trackEngagement,
    trackAbExperiment,
  }
}
